/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { UserSubtenant } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedUserSubtenantsQuery } from '@/hooks/query/usePaginatedUserSubtenantsQuery';
import { LoadingSubtenants } from '@/pages/Subtenants/SubtenantHierarchy/LoadingSubtenants';
import { NoSubtenants } from '@/pages/Subtenants/SubtenantHierarchy/NoSubtenants';
import { useUserDetailsSubtenantListColumns } from '@/pages/UserDetails/useUserDetailsSubtenantListColumns';
import './UserDetailsSubtenantsHierarchy.css';

export type UserDetailsSubtenantsHierarchyProps = {
  tenantID: string;
  userId: string;
  parentSubtenant?: UserSubtenant;
  nestingLevel?: number;
};

export function UserDetailsSubtenantsHierarchy({
  tenantID,
  userId,
  parentSubtenant,
  nestingLevel = 0,
}: UserDetailsSubtenantsHierarchyProps) {
  const userSubtenantsQuery = usePaginatedUserSubtenantsQuery({
    type: parentSubtenant ? 'inMemory' : 'queryParams',
    params: { id: userId, filters: { subtenantId: parentSubtenant?.subtenant.id } },
  });

  const columns = useUserDetailsSubtenantListColumns(tenantID);

  const loadingComponent = parentSubtenant ? <LoadingSubtenants /> : undefined;

  return (
    <div
      className={['user-details-subtenants-hierarchy', `nesting-level-${nestingLevel}`].join(' ')}
      style={{ width: '100%' }}
    >
      <QueryWrapper queryResult={userSubtenantsQuery} loadingComponent={loadingComponent}>
        {(userSubtenantsData) => {
          if (parentSubtenant && !userSubtenantsData.data.length) {
            return <NoSubtenants />;
          }

          return (
            <PaginatedTable
              data={userSubtenantsData}
              hidePaginationInfo
              onParamsChange={userSubtenantsQuery.updateQueryParams}
              rowKey={(subtenant) => subtenant.subtenant.id}
              columns={columns}
              data-testid={`user-details-subtenants-hierarchy-${parentSubtenant?.subtenant.id || 'root'}`}
              expandable={{
                // we have to explicitly set this to something other than 'children' or undefined
                // otherwise AntD ignores the fact that we have a custom expandedRowRender
                // and duplicates the rows (shows the children as it usually does plus the expandedRowRender)
                childrenColumnName: 'not-children',
                rowExpandable: (record) => !!record.subtenant.subtenants,
                // eslint-disable-next-line react/no-unstable-nested-components
                expandedRowRender: (record: UserSubtenant, _idx: number, indent: number, expanded: boolean) => {
                  if (!expanded) return null;
                  return (
                    <UserDetailsSubtenantsHierarchy
                      tenantID={tenantID}
                      userId={userId}
                      parentSubtenant={record}
                      nestingLevel={nestingLevel + indent}
                    />
                  );
                },
              }}
            />
          );
        }}
      </QueryWrapper>
    </div>
  );
}
